import { useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  BadgeEuro,
  Check,
  CheckCircle2,
  Clock3,
  Cloud,
  CloudCog,
  Database,
  Info,
  KeyRound,
  RefreshCw,
  Save,
  ShieldCheck,
  SlidersHorizontal,
  TestTube2,
} from "lucide-react";
import type { PageContext } from "../App";
import { api, getApiError } from "../api";
import {
  Button,
  Field,
  InlineError,
  Input,
  MarketplaceMark,
  PageHeader,
  Panel,
  Skeleton,
  StatusPill,
  Textarea,
  formatDate,
} from "../components/ui";
import { useAsync } from "../hooks";
import {
  MARKETPLACE_CODES,
  MARKETPLACE_META,
  type AppSettings,
  type MarketplaceCode,
} from "../types";

const defaultSettings: AppSettings = {
  priceGuard: {
    minimumPercent: 5,
    minimumAbsoluteEur: 2.5,
    ending: 0.95,
  },
  marketplaceBuffers: {},
  compliance: {},
  syncHours: [6, 10, 14, 22],
  timezone: "Europe/Berlin",
};

export function SettingsPage({ notify }: PageContext) {
  const settingsQuery = useAsync(api.settings, []);
  const connection = useAsync(api.connection, []);
  const marketplaces = useAsync(api.marketplaces, []);
  const [settings, setSettings] = useState<AppSettings>(defaultSettings);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [refreshingMarkets, setRefreshingMarkets] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!settingsQuery.data) return;
    setSettings({
      ...defaultSettings,
      ...settingsQuery.data,
      priceGuard: {
        ...defaultSettings.priceGuard,
        ...settingsQuery.data.priceGuard,
      },
      marketplaceBuffers: settingsQuery.data.marketplaceBuffers ?? {},
      compliance: settingsQuery.data.compliance ?? {},
      syncHours: settingsQuery.data.syncHours ?? [6, 10, 14, 22],
      timezone: settingsQuery.data.timezone ?? "Europe/Berlin",
    });
    setDirty(false);
  }, [settingsQuery.data]);

  const update = (next: AppSettings) => {
    setSettings(next);
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const result = await api.updateSettings(settings);
      setSettings(result);
      setDirty(false);
      notify("Einstellungen wurden gespeichert.", "success");
    } catch (reason) {
      setError(getApiError(reason));
    } finally {
      setSaving(false);
    }
  };

  const testConnection = async () => {
    setTesting(true);
    setError(null);
    try {
      const result = await api.testConnection();
      connection.setData(result);
      notify(
        result.status === "connected"
          ? "Amazon-Verbindung ist einsatzbereit."
          : result.message ?? "Amazon-Verbindung ist eingeschränkt.",
        result.status === "connected" ? "success" : "info",
      );
    } catch (reason) {
      setError(getApiError(reason));
    } finally {
      setTesting(false);
    }
  };

  const refreshMarketplaces = async () => {
    setRefreshingMarkets(true);
    setError(null);
    try {
      marketplaces.setData(await api.refreshMarketplaces());
      notify("Marketplace-Freigaben wurden von Amazon aktualisiert.", "success");
    } catch (reason) {
      setError(getApiError(reason));
    } finally {
      setRefreshingMarkets(false);
    }
  };

  const complianceReady = MARKETPLACE_CODES.filter((code) => {
    const gate = settings.compliance?.[code];
    return gate?.vatReady && gate?.eprReady && gate?.gpsrReady && gate?.enabledForWrites;
  }).length;

  return (
    <div className="settings-page">
      <PageHeader
        eyebrow="Einstellungen"
        title="Amazon-Verbindung, Länderfreigaben & Schutzregeln"
        description="Steuere, welche Marktplätze beschrieben werden dürfen und wie Auslandspreise sicher vorbereitet werden."
        actions={
          <Button
            loading={saving}
            disabled={!dirty}
            onClick={save}
            icon={<Save size={17} />}
          >
            Änderungen speichern
          </Button>
        }
      />

      {error && <InlineError message={error} />}
      {settingsQuery.error && (
        <InlineError message={settingsQuery.error} onRetry={() => void settingsQuery.reload()} />
      )}

      <div className="settings-grid">
        <Panel className="settings-section settings-section--connection">
          <div className="settings-section__heading">
            <span className="settings-section__icon"><Cloud size={21} /></span>
            <div>
              <p className="eyebrow">Amazon SP-API</p>
              <h2>Verbindung</h2>
              <p>Zugangsdaten werden nur serverseitig feldweise aus dem Vault gelesen.</p>
            </div>
            {connection.loading ? (
              <span className="skeleton skeleton--pill" />
            ) : connection.error ? (
              <StatusPill status="disconnected" />
            ) : (
              <StatusPill status={connection.data?.status ?? "neutral"} />
            )}
          </div>
          <div className="connection-detail-grid">
            <div><span>Region</span><strong>{connection.data?.region ?? "Europa"}</strong></div>
            <div><span>Seller-ID</span><strong>{connection.data?.sellerIdMasked ?? "••••••••"}</strong></div>
            <div><span>Letzter Test</span><strong>{formatDate(connection.data?.lastTestAt)}</strong></div>
            <div><span>Letzter Erfolg</span><strong>{formatDate(connection.data?.lastSuccessfulSyncAt)}</strong></div>
          </div>
          {connection.data?.message && (
            <div className="settings-info"><Info size={16} /> {connection.data.message}</div>
          )}
          <div className="settings-section__footer">
            <span><KeyRound size={15} /> Secrets werden nie im Browser angezeigt.</span>
            <Button
              variant="secondary"
              loading={testing}
              onClick={testConnection}
              icon={<TestTube2 size={16} />}
            >
              Verbindung testen
            </Button>
          </div>
        </Panel>

        <Panel className="settings-section settings-section--schedule">
          <div className="settings-section__heading">
            <span className="settings-section__icon"><Clock3 size={21} /></span>
            <div>
              <p className="eyebrow">Zeitplan</p>
              <h2>Amazon-Abgleich</h2>
              <p>Vier feste Läufe in der Zeitzone Europe/Berlin.</p>
            </div>
            <StatusPill status="success" label="Aktiv" />
          </div>
          <div className="schedule-timeline">
            {[6, 10, 14, 22].map((hour, index) => (
              <div key={hour} className={index === 0 ? "is-primary" : ""}>
                <span><Clock3 size={15} /></span>
                <strong>{String(hour).padStart(2, "0")}:00</strong>
                <small>{index === 0 ? "Morgenlauf" : index === 3 ? "Tagesabschluss" : "Vollabgleich"}</small>
              </div>
            ))}
          </div>
          <div className="night-rest">
            <CloudCog size={17} />
            <span><strong>Ruhezeit 22:00–06:00</strong>Keine geplanten Arbeiten in diesem Fenster.</span>
          </div>
        </Panel>
      </div>

      <Panel className="settings-section compliance-settings">
        <div className="settings-section__heading">
          <span className="settings-section__icon"><ShieldCheck size={21} /></span>
          <div>
            <p className="eyebrow">Schreibschutz</p>
            <h2>Compliance-Ampel je Marketplace</h2>
            <p>
              Nur vollständig geprüfte Länder dürfen im Uploader oder Aktualisierer als
              Schreibziel verwendet werden.
            </p>
          </div>
          <span className="completion-counter">{complianceReady}/{MARKETPLACE_CODES.length} grün</span>
        </div>

        {settingsQuery.loading ? (
          <Skeleton lines={5} />
        ) : (
          <div className="compliance-table-wrap">
            <table className="compliance-table">
              <thead>
                <tr>
                  <th>Marketplace</th>
                  <th>VAT / OSS</th>
                  <th>EPR</th>
                  <th>GPSR</th>
                  <th>Schreiben</th>
                  <th>Interne Notiz</th>
                </tr>
              </thead>
              <tbody>
                {MARKETPLACE_CODES.map((code) => {
                  const gate = settings.compliance?.[code] ?? {};
                  const prerequisites = Boolean(gate.vatReady && gate.eprReady && gate.gpsrReady);
                  return (
                    <tr key={code}>
                      <td>
                        <MarketplaceMark code={code} decorative />
                        <span>
                          <strong>{MARKETPLACE_META[code].label}</strong>
                          <small>{MARKETPLACE_META[code].vat}% MwSt.</small>
                        </span>
                      </td>
                      <td>
                        <GateToggle
                          label={`${code} VAT/OSS freigegeben`}
                          checked={Boolean(gate.vatReady)}
                          onChange={(checked) =>
                            updateCompliance(settings, update, code, { vatReady: checked })
                          }
                        />
                      </td>
                      <td>
                        <GateToggle
                          label={`${code} EPR freigegeben`}
                          checked={Boolean(gate.eprReady)}
                          onChange={(checked) =>
                            updateCompliance(settings, update, code, { eprReady: checked })
                          }
                        />
                      </td>
                      <td>
                        <GateToggle
                          label={`${code} GPSR freigegeben`}
                          checked={Boolean(gate.gpsrReady)}
                          onChange={(checked) =>
                            updateCompliance(settings, update, code, { gpsrReady: checked })
                          }
                        />
                      </td>
                      <td>
                        <button
                          type="button"
                          className={`write-gate ${gate.enabledForWrites ? "is-enabled" : ""}`}
                          disabled={!prerequisites}
                          onClick={() =>
                            updateCompliance(settings, update, code, {
                              enabledForWrites: !gate.enabledForWrites,
                            })
                          }
                        >
                          {gate.enabledForWrites ? <Check size={14} /> : <AlertTriangle size={14} />}
                          {gate.enabledForWrites ? "Freigegeben" : prerequisites ? "Gesperrt" : "Prüfung fehlt"}
                        </button>
                      </td>
                      <td>
                        <Input
                          value={gate.note ?? ""}
                          onChange={(event) =>
                            updateCompliance(settings, update, code, { note: event.target.value })
                          }
                          placeholder="optional"
                          aria-label={`Notiz zu ${code}`}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <div className="compliance-note">
          <AlertTriangle size={17} />
          <p>
            Diese Ampel ist eine betriebliche Freigabe, keine automatische Rechtsberatung.
            Änderungen werden protokolliert und serverseitig vor jedem Amazon-Write erneut geprüft.
          </p>
        </div>
      </Panel>

      <Panel className="settings-section marketplace-settings">
        <div className="settings-section__heading">
          <span className="settings-section__icon"><Database size={21} /></span>
          <div>
            <p className="eyebrow">Amazon Seller Central</p>
            <h2>Verfügbare Marktplätze</h2>
            <p>Die Teilnahme wird direkt aus Amazon gelesen, nicht hart vorausgesetzt.</p>
          </div>
          <Button
            variant="secondary"
            loading={refreshingMarkets}
            onClick={refreshMarketplaces}
            icon={<RefreshCw size={16} />}
          >
            Von Amazon aktualisieren
          </Button>
        </div>
        {marketplaces.error ? (
          <InlineError message={marketplaces.error} onRetry={() => void marketplaces.reload()} />
        ) : marketplaces.loading ? (
          <Skeleton lines={3} />
        ) : (
          <div className="marketplace-settings-grid">
            {MARKETPLACE_CODES.map((code) => {
              const marketplace = marketplaces.data?.find((item) => item.code === code);
              return (
                <article key={code}>
                  <MarketplaceMark code={code} decorative />
                  <div>
                    <strong>{MARKETPLACE_META[code].label}</strong>
                    <small>{MARKETPLACE_META[code].marketplaceId}</small>
                  </div>
                  <StatusPill
                    status={marketplace?.connected && marketplace.enabled ? "connected" : "neutral"}
                    label={
                      marketplace?.connected && marketplace.enabled
                        ? "Amazon aktiv"
                        : "Nicht verfügbar"
                    }
                  />
                </article>
              );
            })}
          </div>
        )}
      </Panel>

      <Panel className="settings-section price-settings">
        <div className="settings-section__heading">
          <span className="settings-section__icon"><BadgeEuro size={21} /></span>
          <div>
            <p className="eyebrow">Startpreise</p>
            <h2>Auslandspreis-Schutzregel</h2>
            <p>Die Regel gilt nur beim neuen Upload oder Länder-Rollout.</p>
          </div>
          <StatusPill status="warning" label="Repricer bleibt zuständig" />
        </div>

        <div className="price-settings-grid">
          <Field label="Mindestaufschlag in Prozent">
            <div className="money-input">
              <Input
                type="number"
                min="0"
                step="0.1"
                value={settings.priceGuard?.minimumPercent ?? 5}
                onChange={(event) =>
                  update({
                    ...settings,
                    priceGuard: {
                      ...settings.priceGuard,
                      minimumPercent: Number(event.target.value),
                    },
                  })
                }
              />
              <span>%</span>
            </div>
          </Field>
          <Field label="Absoluter Mindestaufschlag">
            <div className="money-input">
              <Input
                type="number"
                min="0"
                step="0.1"
                value={settings.priceGuard?.minimumAbsoluteEur ?? 2.5}
                onChange={(event) =>
                  update({
                    ...settings,
                    priceGuard: {
                      ...settings.priceGuard,
                      minimumAbsoluteEur: Number(event.target.value),
                    },
                  })
                }
              />
              <span>EUR</span>
            </div>
          </Field>
          <Field label="Preis-Endung">
            <div className="money-input">
              <Input value=",95" readOnly />
              <span>fest</span>
            </div>
          </Field>
        </div>

        <div className="buffer-table-wrap">
          <table className="buffer-table">
            <thead><tr><th>Land</th><th>Standard-MwSt.</th><th>Versandpuffer</th><th>Gebührenpuffer</th></tr></thead>
            <tbody>
              {MARKETPLACE_CODES.filter((code) => code !== "DE").map((code) => {
                const buffer = settings.marketplaceBuffers?.[code] ?? {};
                return (
                  <tr key={code}>
                    <td><MarketplaceMark code={code} /></td>
                    <td>{MARKETPLACE_META[code].vat}%</td>
                    <td>
                      <div className="compact-money-input">
                        <Input
                          type="number"
                          min="0"
                          step="0.1"
                          value={buffer.shippingEur ?? 0}
                          onChange={(event) =>
                            updateBuffer(settings, update, code, {
                              shippingEur: Number(event.target.value),
                            })
                          }
                        />
                        <span>€</span>
                      </div>
                    </td>
                    <td>
                      <div className="compact-money-input">
                        <Input
                          type="number"
                          min="0"
                          step="0.1"
                          value={buffer.feePercent ?? 0}
                          onChange={(event) =>
                            updateBuffer(settings, update, code, {
                              feePercent: Number(event.target.value),
                            })
                          }
                        />
                        <span>%</span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="settings-info">
          <SlidersHorizontal size={16} />
          Es werden keine Min-/Max-Preise bei Amazon oder im Repricer verändert.
        </div>
      </Panel>

      <div className="sticky-save">
        <span>
          {dirty ? <AlertTriangle size={16} /> : <CheckCircle2 size={16} />}
          {dirty ? "Es gibt ungespeicherte Änderungen." : "Alle Einstellungen sind gespeichert."}
        </span>
        <Button
          loading={saving}
          disabled={!dirty}
          onClick={save}
          icon={<Save size={16} />}
        >
          Speichern
        </Button>
      </div>
    </div>
  );
}

function GateToggle({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
}) {
  return (
    <label className="gate-toggle" title={label}>
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        aria-label={label}
      />
      <span>{checked && <Check size={13} />}</span>
    </label>
  );
}

function updateCompliance(
  settings: AppSettings,
  update: (settings: AppSettings) => void,
  code: MarketplaceCode,
  patch: Record<string, unknown>,
) {
  const current = settings.compliance?.[code] ?? {};
  const next = { ...current, ...patch };
  const allReady = next.vatReady && next.eprReady && next.gpsrReady;
  if (!allReady) next.enabledForWrites = false;
  update({
    ...settings,
    compliance: {
      ...settings.compliance,
      [code]: next,
    },
  });
}

function updateBuffer(
  settings: AppSettings,
  update: (settings: AppSettings) => void,
  code: MarketplaceCode,
  patch: { shippingEur?: number; feePercent?: number },
) {
  update({
    ...settings,
    marketplaceBuffers: {
      ...settings.marketplaceBuffers,
      [code]: {
        ...settings.marketplaceBuffers?.[code],
        ...patch,
      },
    },
  });
}
